import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { useProjectsStore } from '../store/projects';

interface AIGeneration {
  id: string;
  display_id: string;
  project_id: string;
  flight_id: string | null;
  template_id: string;
  prompt_data: {
    briefing: {
      product_name: string;
      product_description?: string;
      target_audience?: string;
      key_benefits?: string;
      tone_of_voice?: string;
      hashtags?: string;
    };
    post_count: number;
    platforms: string[];
  };
  result_data: {
    posts?: Array<{
      id: string;
      text: string;
      image_url?: string;
      status: string;
    }>;
  } | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
  posts_generated: number;
  created_at: string;
}

interface ReviewPost {
  id: string;
  text: string;
  image_url?: string;
  platforms: string[];
  post_type: string;
  status: 'pending' | 'approved' | 'rejected' | 'edited';
  scheduled_for?: string;
  original_text?: string;
}

export function AIReview() {
  const { t } = useTranslation(['posts', 'common']);
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { currentProject, fetchProject } = useProjectsStore();
  const [generations, setGenerations] = useState<AIGeneration[]>([]);
  const [selectedGeneration, setSelectedGeneration] = useState<AIGeneration | null>(null);
  const [reviewPosts, setReviewPosts] = useState<ReviewPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [previewPost, setPreviewPost] = useState<ReviewPost | null>(null);
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    if (projectId) {
      fetchGenerations();
      fetchProject(projectId);
    }
  }, [projectId, fetchProject]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (previewPost) setPreviewPost(null);
        if (editingPost) setEditingPost(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewPost, editingPost]);

  const fetchGenerations = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<{ data: AIGeneration[] }>(`/creator/ai-generations?project_id=${projectId}`);
      setGenerations(response.data || []);
      if (response.data?.length > 0) {
        setSelectedGeneration(response.data[0]);
        loadReviewPosts(response.data[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('posts:aiReview.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadReviewPosts = (generation: AIGeneration) => {
    if (generation.result_data?.posts) {
      setReviewPosts(generation.result_data.posts.map(p => ({
        ...p,
        platforms: generation.prompt_data?.platforms || ['instagram'],
        post_type: 'reel',
        status: 'pending' as const,
        original_text: p.text,
      })));
      setSelectedPosts(new Set());
    } else {
      setReviewPosts([]);
    }
  };

  const handleApprove = useCallback((postId: string) => {
    setReviewPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, status: 'approved' as const } : p
    ));
  }, []);

  const handleReject = useCallback((postId: string) => {
    setReviewPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, status: 'rejected' as const } : p
    ));
  }, []);

  const handleResetStatus = useCallback((postId: string) => {
    setReviewPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, status: 'pending' as const } : p
    ));
  }, []);

  const handleEdit = (postId: string) => {
    const post = reviewPosts.find(p => p.id === postId);
    if (post) {
      setEditingPost(postId);
      setEditText(post.text);
    }
  };

  const handleSaveEdit = (postId: string) => {
    setReviewPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, text: editText, status: 'edited' as const } : p
    ));
    setEditingPost(null);
    setEditText('');
  };

  const handleApproveAll = () => {
    setReviewPosts(prev => prev.map(p =>
      p.status === 'pending' ? { ...p, status: 'approved' as const } : p
    ));
  };

  const handleApproveSelected = () => {
    setReviewPosts(prev => prev.map(p =>
      selectedPosts.has(p.id) ? { ...p, status: 'approved' as const } : p
    ));
    setSelectedPosts(new Set());
  };

  const handleRejectSelected = () => {
    setReviewPosts(prev => prev.map(p =>
      selectedPosts.has(p.id) ? { ...p, status: 'rejected' as const } : p
    ));
    setSelectedPosts(new Set());
  };

  const togglePostSelection = (postId: string) => {
    setSelectedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedPosts.size === reviewPosts.length) {
      setSelectedPosts(new Set());
    } else {
      setSelectedPosts(new Set(reviewPosts.map(p => p.id)));
    }
  };

  const handleSaveApproved = async () => {
    const approvedPosts = reviewPosts.filter(p => p.status === 'approved' || p.status === 'edited');
    if (approvedPosts.length === 0) {
      setError(t('posts:aiReview.approveAtLeastOne'));
      return;
    }

    try {
      setIsSaving(true);
      await api.post('/posts/batch/save-generated', {
        project_id: projectId,
        posts: approvedPosts.map(p => ({
          text: p.text,
          platforms: p.platforms,
          post_type: p.post_type,
          status: 'draft',
          media_urls: p.image_url ? [p.image_url] : [],
        })),
      });
      navigate(`/projects/${projectId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('posts:aiReview.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportLocal = () => {
    const exportData = {
      exported_at: new Date().toISOString(),
      generation: selectedGeneration,
      posts: reviewPosts,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-review-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getImageUrl = (url?: string) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // Use relative URL which will be handled by proxy
    return url;
  };

  const approvedCount = reviewPosts.filter(p => p.status === 'approved' || p.status === 'edited').length;
  const pendingCount = reviewPosts.filter(p => p.status === 'pending').length;
  const rejectedCount = reviewPosts.filter(p => p.status === 'rejected').length;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        }
        title={t('posts:aiReview.title')}
        subtitle={t('posts:aiReview.subtitle')}
        breadcrumbs={currentProject ? [
          { label: t('common:nav.projects'), href: '/projects' },
          { label: currentProject.name, href: `/projects/${currentProject.id}` },
        ] : [{ label: 'Dashboard', href: '/' }]}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
              >
                <GridIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
              >
                <ListIcon className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleExportLocal}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('posts:aiReview.jsonExport')}
            </button>
            <button
              onClick={handleApproveAll}
              disabled={pendingCount === 0}
              className="px-4 py-2 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 disabled:opacity-50"
            >
              {t('posts:aiReview.approveAll')} ({pendingCount})
            </button>
            <button
              onClick={handleSaveApproved}
              disabled={approvedCount === 0 || isSaving}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  {t('common:saving')}
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  {t('posts:aiReview.saveCount', { count: approvedCount })}
                </>
              )}
            </button>
          </div>
        }
      />

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {generations.length === 0 ? (
        <EmptyState projectId={projectId} t={t} />
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar: Generation List */}
          <div className="col-span-12 lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">{t('posts:aiReview.generations')}</h3>
                <p className="text-xs text-gray-500 mt-1">{t('posts:aiReview.available', { count: generations.length })}</p>
              </div>
              <div className="divide-y divide-gray-100 max-h-[calc(100vh-300px)] overflow-y-auto">
                {generations.map(gen => (
                  <button
                    key={gen.id}
                    onClick={() => {
                      setSelectedGeneration(gen);
                      loadReviewPosts(gen);
                    }}
                    className={`w-full text-left p-4 transition hover:bg-gray-50 ${
                      selectedGeneration?.id === gen.id ? 'bg-purple-50 border-l-4 border-purple-500' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900 truncate">
                      {gen.prompt_data?.briefing?.product_name || 'AI Content'}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span>{t('posts:aiReview.posts', { count: gen.posts_generated })}</span>
                      <span>•</span>
                      <span>{new Date(gen.created_at).toLocaleDateString('de-DE')}</span>
                    </div>
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full ${
                      gen.status === 'completed' ? 'bg-green-100 text-green-700' :
                      gen.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {gen.status === 'completed' ? t('posts:aiReview.statusComplete') : gen.status === 'failed' ? t('posts:aiReview.statusFailed') : t('posts:aiReview.statusInProgress')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main: Post Review */}
          <div className="col-span-12 lg:col-span-9">
            {/* Stats Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-sm text-gray-700">{approvedCount} {t('posts:aiReview.approved')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-sm text-gray-700">{pendingCount} {t('posts:aiReview.pending')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-sm text-gray-700">{rejectedCount} {t('posts:aiReview.rejected')}</span>
                  </div>
                </div>
                {/* Bulk Actions */}
                {selectedPosts.size > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{t('posts:aiReview.selected', { count: selectedPosts.size })}</span>
                    <button
                      onClick={handleApproveSelected}
                      className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      {t('posts:aiReview.approveSelected')}
                    </button>
                    <button
                      onClick={handleRejectSelected}
                      className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      {t('posts:aiReview.rejectSelected')}
                    </button>
                  </div>
                )}
                {reviewPosts.length > 0 && (
                  <button
                    onClick={selectAll}
                    className="text-sm text-purple-600 hover:text-purple-700"
                  >
                    {selectedPosts.size === reviewPosts.length ? t('posts:aiReview.deselectAll') : t('posts:aiReview.selectAll')}
                  </button>
                )}
              </div>
            </div>

            {/* Posts */}
            {reviewPosts.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <SparklesIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('posts:aiReview.noPostsInGeneration')}</h3>
                <p className="text-gray-500">
                  {t('posts:aiReview.noPostsDescription')}
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {reviewPosts.map((post, index) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    index={index}
                    isSelected={selectedPosts.has(post.id)}
                    isEditing={editingPost === post.id}
                    editText={editText}
                    onSelect={() => togglePostSelection(post.id)}
                    onApprove={() => handleApprove(post.id)}
                    onReject={() => handleReject(post.id)}
                    onReset={() => handleResetStatus(post.id)}
                    onEdit={() => handleEdit(post.id)}
                    onSaveEdit={() => handleSaveEdit(post.id)}
                    onCancelEdit={() => setEditingPost(null)}
                    onEditTextChange={setEditText}
                    onPreview={() => setPreviewPost(post)}
                    getImageUrl={getImageUrl}
                    t={t}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
                {reviewPosts.map((post, index) => (
                  <PostListItem
                    key={post.id}
                    post={post}
                    index={index}
                    isSelected={selectedPosts.has(post.id)}
                    onSelect={() => togglePostSelection(post.id)}
                    onApprove={() => handleApprove(post.id)}
                    onReject={() => handleReject(post.id)}
                    onReset={() => handleResetStatus(post.id)}
                    onEdit={() => handleEdit(post.id)}
                    onPreview={() => setPreviewPost(post)}
                    getImageUrl={getImageUrl}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewPost && (
        <PreviewModal
          post={previewPost}
          onClose={() => setPreviewPost(null)}
          onApprove={() => { handleApprove(previewPost.id); setPreviewPost(null); }}
          onReject={() => { handleReject(previewPost.id); setPreviewPost(null); }}
          getImageUrl={getImageUrl}
          t={t}
        />
      )}
    </div>
  );
}

// Post Card Component
function PostCard({
  post,
  index,
  isSelected,
  isEditing,
  editText,
  onSelect,
  onApprove,
  onReject,
  onReset,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onEditTextChange,
  onPreview,
  getImageUrl,
  t,
}: {
  post: ReviewPost;
  index: number;
  isSelected: boolean;
  isEditing: boolean;
  editText: string;
  onSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  onReset: () => void;
  onEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditTextChange: (text: string) => void;
  onPreview: () => void;
  getImageUrl: (url?: string) => string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const statusStyles = {
    approved: 'border-green-400 bg-green-50',
    edited: 'border-blue-400 bg-blue-50',
    rejected: 'border-red-400 bg-red-50 opacity-60',
    pending: 'border-gray-200',
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm overflow-hidden border-2 transition ${statusStyles[post.status]}`}>
      {/* Selection Checkbox */}
      <div className="absolute top-3 left-3 z-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
      </div>

      {/* Image */}
      <div className="relative group cursor-pointer" onClick={onPreview}>
        {post.image_url ? (
          <img
            src={getImageUrl(post.image_url) || ''}
            alt={`${t('posts:aiReview.post')} ${index + 1}`}
            className="w-full aspect-[4/5] object-cover"
          />
        ) : (
          <div className="w-full aspect-[4/5] bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
            <SparklesIcon className="w-12 h-12 text-purple-300" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center">
          <EyeIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition" />
        </div>
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            post.status === 'approved' ? 'bg-green-500 text-white' :
            post.status === 'edited' ? 'bg-blue-500 text-white' :
            post.status === 'rejected' ? 'bg-red-500 text-white' :
            'bg-gray-100 text-gray-600'
          }`}>
            {post.status === 'approved' ? 'OK' :
             post.status === 'edited' ? t('posts:aiReview.edited') :
             post.status === 'rejected' ? t('posts:aiReview.rejected') : `#${index + 1}`}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              value={editText}
              onChange={(e) => onEditTextChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows={4}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={onSaveEdit}
                className="flex-1 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {t('common:save')}
              </button>
              <button
                onClick={onCancelEdit}
                className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                {t('common:cancel')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-700 line-clamp-3 mb-4">{post.text}</p>

            {/* Actions */}
            <div className="flex gap-2">
              {post.status === 'rejected' ? (
                <button
                  onClick={onReset}
                  className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-1"
                >
                  <RefreshIcon className="w-4 h-4" />
                  {t('common:reset')}
                </button>
              ) : (
                <>
                  <button
                    onClick={onApprove}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg flex items-center justify-center gap-1 transition ${
                      post.status === 'approved' || post.status === 'edited'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-green-100 hover:text-green-700'
                    }`}
                  >
                    <CheckIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onEdit}
                    className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-blue-100 hover:text-blue-700 flex items-center justify-center gap-1"
                  >
                    <EditIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onReject}
                    className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-red-100 hover:text-red-700 flex items-center justify-center gap-1"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Post List Item Component
function PostListItem({
  post,
  index,
  isSelected,
  onSelect,
  onApprove,
  onReject,
  onReset,
  onEdit,
  onPreview,
  getImageUrl,
  t,
}: {
  post: ReviewPost;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  onReset: () => void;
  onEdit: () => void;
  onPreview: () => void;
  getImageUrl: (url?: string) => string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <div className={`p-4 flex items-start gap-4 hover:bg-gray-50 ${post.status === 'rejected' ? 'opacity-60' : ''}`}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onSelect}
        className="mt-1 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
      />

      {post.image_url ? (
        <img
          src={getImageUrl(post.image_url) || ''}
          alt={`${t('posts:aiReview.post')} ${index + 1}`}
          className="w-16 h-20 object-cover rounded cursor-pointer"
          onClick={onPreview}
        />
      ) : (
        <div
          className="w-16 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded flex items-center justify-center cursor-pointer"
          onClick={onPreview}
        >
          <SparklesIcon className="w-6 h-6 text-purple-300" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-gray-500">{t('posts:aiReview.post')} {index + 1}</span>
          <span className={`px-2 py-0.5 text-xs rounded-full ${
            post.status === 'approved' ? 'bg-green-100 text-green-700' :
            post.status === 'edited' ? 'bg-blue-100 text-blue-700' :
            post.status === 'rejected' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-600'
          }`}>
            {post.status === 'approved' ? t('posts:aiReview.approved') :
             post.status === 'edited' ? t('posts:aiReview.edited') :
             post.status === 'rejected' ? t('posts:aiReview.rejected') : t('posts:aiReview.pending')}
          </span>
        </div>
        <p className="text-sm text-gray-700 line-clamp-2">{post.text}</p>
      </div>

      <div className="flex items-center gap-1">
        {post.status === 'rejected' ? (
          <button
            onClick={onReset}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title={t('posts:aiReview.resetStatus')}
          >
            <RefreshIcon className="w-5 h-5" />
          </button>
        ) : (
          <>
            <button
              onClick={onApprove}
              className={`p-2 rounded ${
                post.status === 'approved' || post.status === 'edited'
                  ? 'text-green-600 bg-green-100'
                  : 'text-gray-400 hover:text-green-600 hover:bg-green-100'
              }`}
              title={t('posts:aiReview.approve')}
            >
              <CheckIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded"
              title={t('common:edit')}
            >
              <EditIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onReject}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded"
              title={t('posts:aiReview.reject')}
            >
              <XIcon className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Preview Modal
function PreviewModal({
  post,
  onClose,
  onApprove,
  onReject,
  getImageUrl,
  t,
}: {
  post: ReviewPost;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  getImageUrl: (url?: string) => string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {post.image_url ? (
          <img
            src={getImageUrl(post.image_url) || ''}
            alt="Preview"
            className="w-full max-h-[60vh] object-contain bg-gray-100"
          />
        ) : (
          <div className="w-full h-64 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
            <SparklesIcon className="w-16 h-16 text-purple-300" />
          </div>
        )}
        <div className="p-6">
          <p className="text-gray-700 whitespace-pre-wrap mb-6">{post.text}</p>
          <div className="flex gap-3">
            <button
              onClick={onApprove}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <CheckIcon className="w-5 h-5" />
              {t('posts:aiReview.approve')}
            </button>
            <button
              onClick={onReject}
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
            >
              <XIcon className="w-5 h-5" />
              {t('posts:aiReview.reject')}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              {t('common:close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Empty State
function EmptyState({ projectId, t }: { projectId?: string; t: (key: string, options?: Record<string, unknown>) => string }) {
  const navigate = useNavigate();

  return (
    <div className="text-center py-16 px-4">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-100 flex items-center justify-center">
        <SparklesIcon className="w-10 h-10 text-purple-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('posts:aiReview.noGenerations')}</h3>
      <p className="text-gray-500 max-w-md mx-auto mb-8">
        {t('posts:aiReview.noGenerationsDescription')}
      </p>
      <button
        onClick={() => navigate(`/creator?project=${projectId}`)}
        className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 inline-flex items-center gap-2"
      >
        <SparklesIcon className="w-5 h-5" />
        {t('posts:aiReview.toCreatorStudio')}
      </button>
    </div>
  );
}

// Icons
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function GridIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function ListIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
}
